import { useState, useEffect } from 'react';
import { instrumentAPI } from '../../../../../services';
import './BulkPresetModal.css';

function BulkPresetModal({ instruments, onClose, onSave, onOpenPresetManager }) {
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [instrumentFilter, setInstrumentFilter] = useState('all');
  const [mergeHolidays, setMergeHolidays] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [results, setResults] = useState({ successful: [], failed: [] });

  const instrumentTypes = ['all', 'forex', 'crypto', 'stocks'];

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const response = await instrumentAPI.getTradingHoursPresets();
      if (response.data && response.data.presets) {
        setPresets(response.data.presets);
      }
    } catch (err) {
      console.error('Error fetching presets:', err);
      setPresets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInstrumentSelect = (instrumentId) => {
    setSelectedInstruments(prev => 
      prev.includes(instrumentId)
        ? prev.filter(id => id !== instrumentId)
        : [...prev, instrumentId]
    );
  };

  const handleSelectAll = () => {
    const filteredInstruments = getFilteredInstruments();
    if (selectedInstruments.length === filteredInstruments.length) {
      setSelectedInstruments([]);
    } else {
      setSelectedInstruments(filteredInstruments.map(inst => inst._id));
    }
  };

  const handleSelectByType = (type) => {
    const instrumentsOfType = instruments.filter(inst => 
      inst.type.toLowerCase() === type.toLowerCase()
    );
    const idsOfType = instrumentsOfType.map(inst => inst._id);
    setSelectedInstruments(prev => {
      const newSelection = new Set(prev);
      idsOfType.forEach(id => newSelection.add(id));
      return Array.from(newSelection);
    });
  };

  const getFilteredInstruments = () => {
    return instruments.filter(instrument => {
      const matchesSearch = instrument.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          instrument.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = instrumentFilter === 'all' || 
                         instrument.type.toLowerCase() === instrumentFilter.toLowerCase();
      return matchesSearch && matchesType;
    });
  };

  const getFilteredPresets = () => {
    return presets.filter(preset => {
      return filterCategory === 'all' || preset.category === filterCategory;
    });
  };

  const handleApplyPreset = async () => {
    if (!selectedPreset || selectedInstruments.length === 0) return;

    setApplying(true);
    setError(null);
    setResults({ successful: [], failed: [] });

    const selectedInstrumentObjects = instruments.filter(inst => 
      selectedInstruments.includes(inst._id)
    );

    try {
      setProgress({ 
        current: 0, 
        total: selectedInstrumentObjects.length, 
        message: 'Starting bulk application...' 
      });

      const options = mergeHolidays ? { merge_holidays: true } : {};

      // Try bulk endpoint first
      try {
        await instrumentAPI.applyPresetToMultipleInstruments(
          selectedInstruments,
          selectedPreset.name,
          options
        );

        const successful = selectedInstrumentObjects.map(inst => ({
          symbol: inst.symbol,
          description: inst.description
        }));

        setResults({ successful, failed: [] });
        setProgress({ 
          current: selectedInstrumentObjects.length, 
          total: selectedInstrumentObjects.length, 
          message: `Completed: ${successful.length} successful, 0 failed` 
        });
        
        onSave();
      } catch (bulkError) {
        console.warn('Bulk endpoint failed, falling back to individual requests:', bulkError);
        
        // Fallback to individual requests
        const successful = [];
        const failed = [];

        for (let i = 0; i < selectedInstrumentObjects.length; i++) {
          const instrument = selectedInstrumentObjects[i];
          
          setProgress({ 
            current: i, 
            total: selectedInstrumentObjects.length, 
            message: `Applying to ${instrument.symbol}...` 
          });

          try {
            await instrumentAPI.applyPresetToInstrument(instrument.symbol, selectedPreset.name, options);
            successful.push({
              symbol: instrument.symbol,
              description: instrument.description
            });
          } catch (individualError) {
            console.error(`Failed to apply preset to ${instrument.symbol}:`, individualError);
            failed.push({
              symbol: instrument.symbol,
              description: instrument.description,
              error: individualError.message || 'Unknown error'
            });
          }
          
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        setResults({ successful, failed });
        setProgress({ 
          current: selectedInstrumentObjects.length, 
          total: selectedInstrumentObjects.length, 
          message: `Completed: ${successful.length} successful, ${failed.length} failed` 
        });
        
        if (successful.length > 0) {
          onSave();
        }
      }
    } catch (err) {
      console.error('Error applying presets:', err);
      setError(`Failed to apply presets: ${err.message}`);
    } finally {
      setApplying(false);
    }
  };

  const getCategoryIcon = (category) => {
    const categoryMap = {
      forex: 'fas fa-exchange-alt',
      stocks: 'fas fa-chart-line',
      crypto: 'fab fa-bitcoin',
      commodities: 'fas fa-seedling',
      indices: 'fas fa-chart-bar',
      custom: 'fas fa-cog'
    };
    return categoryMap[category] || 'fas fa-cog';
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      forex: '#10b981',
      stocks: '#3b82f6',
      crypto: '#f59e0b',
      commodities: '#8b5cf6',
      indices: '#ef4444',
      custom: '#64748b'
    };
    return colorMap[category] || '#64748b';
  };

  if (loading) {
    return (
      <div className="bpm-overlay">
        <div className="bpm-container">
          <div className="bpm-loading">
            <div className="bpm-spinner"></div>
            <p>Loading presets...</p>
          </div>
        </div>
      </div>
    );
  }

  const filteredInstruments = getFilteredInstruments();
  const filteredPresets = getFilteredPresets();

  return (
    <div className="bpm-overlay">
      <div className="bpm-container">
        {/* Header */}
        <div className="bpm-header">
          <div className="bpm-title">
            <div className="bpm-title-icon">
              <i className="fas fa-magic"></i>
            </div>
            <div className="bpm-title-text">
              <h2>Bulk Apply Preset</h2>
              <p>Apply trading hours preset to multiple instruments at once</p>
            </div>
          </div>
          <button className="bpm-close-btn" onClick={onClose} disabled={applying}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Content */}
        <div className="bpm-content">
          {error && (
            <div className="bpm-error">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Progress */}
          {applying && (
            <div className="bpm-progress">
              <div className="bpm-progress-header">
                <h3>Applying Preset...</h3>
                <span>{progress.current} / {progress.total}</span>
              </div>
              <div className="bpm-progress-bar">
                <div 
                  className="bpm-progress-fill" 
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
              <p className="bpm-progress-message">{progress.message}</p>
            </div>
          )}

          {/* Results */}
          {(results.successful.length > 0 || results.failed.length > 0) ? (
            <div className="bpm-results">
              <h3>Application Results</h3>
              
              {results.successful.length > 0 && (
                <div className="bpm-success-section">
                  <h4><i className="fas fa-check-circle"></i> Successfully Applied ({results.successful.length})</h4>
                  <div className="bpm-results-list">
                    {results.successful.map(item => (
                      <div key={item.symbol} className="bpm-result-item success">
                        <span className="bpm-result-symbol">{item.symbol}</span>
                        <span className="bpm-result-description">{item.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {results.failed.length > 0 && (
                <div className="bpm-error-section">
                  <h4><i className="fas fa-exclamation-circle"></i> Failed to Apply ({results.failed.length})</h4>
                  <div className="bpm-results-list">
                    {results.failed.map(item => (
                      <div key={item.symbol} className="bpm-result-item error">
                        <span className="bpm-result-symbol">{item.symbol}</span>
                        <span className="bpm-result-description">{item.description}</span>
                        <span className="bpm-result-error">{item.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bpm-sections">
              {/* Step 1: Preset Selection */}
              <div className="bpm-section">
                <div className="bpm-section-header">
                  <h3 className="bpm-section-title">
                    <span className="bpm-step-badge">1</span>
                    Select Preset
                  </h3>
                  <div className="bpm-filters">
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="bpm-filter-select"
                    >
                      <option value="all">All Categories</option>
                      <option value="forex">Forex</option>
                      <option value="stocks">Stocks</option>
                      <option value="crypto">Crypto</option>
                      <option value="commodities">Commodities</option>
                      <option value="indices">Indices</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                
                {presets.length > 0 ? (
                  <div className="bpm-preset-grid">
                    {filteredPresets.map(preset => (
                      <div 
                        key={preset.name} 
                        className={`bpm-preset-card ${selectedPreset?.name === preset.name ? 'selected' : ''}`}
                        onClick={() => setSelectedPreset(preset)}
                      >
                        <div className="bpm-preset-header">
                          <div 
                            className="bpm-preset-icon" 
                            style={{ backgroundColor: getCategoryColor(preset.category) }}
                          >
                            <i className={getCategoryIcon(preset.category)}></i>
                          </div>
                          <div className="bpm-preset-badges">
                            <span className="bpm-category-badge">{preset.category}</span>
                            {preset.is_system_preset && (
                              <span className="bpm-system-badge">System</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="bpm-preset-content">
                          <h4>{preset.name}</h4>
                          <p>{preset.description || 'No description provided'}</p>
                          
                          <div className="bpm-preset-stats">
                            <span><i className="fas fa-globe"></i> {preset.trading_hours?.timezone || 'UTC'}</span>
                            <span><i className="fas fa-calendar-week"></i> {preset.trading_hours?.weekly_schedule?.length || 0} days</span>
                            <span><i className="fas fa-calendar-times"></i> {preset.trading_hours?.holidays?.length || 0} holidays</span>
                          </div>
                        </div>
                        
                        {selectedPreset?.name === preset.name && (
                          <div className="bpm-selected-indicator">
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bpm-empty preset-empty">
                    <i className="fas fa-magic"></i>
                    <p>No presets available. You need to create at least one preset before you can apply it to instruments.</p>
                    {onOpenPresetManager && (
                      <button 
                        className="bpm-btn-create"
                        onClick={() => {
                          onClose();
                          onOpenPresetManager();
                        }}
                      >
                        <i className="fas fa-plus"></i>
                        Create First Preset
                      </button>
                    )}
                  </div>
                )}
                
                {presets.length > 0 && filteredPresets.length === 0 && (
                  <div className="bpm-empty">
                    <i className="fas fa-search"></i>
                    <p>No presets found for the selected category</p>
                  </div>
                )}
              </div>

              {/* Step 2: Instrument Selection */}
              <div className="bpm-section">
                <div className="bpm-section-header">
                  <h3 className="bpm-section-title">
                    <span className="bpm-step-badge">2</span>
                    Select Instruments ({selectedInstruments.length} selected)
                  </h3>
                  <div className="bpm-filters">
                    <div className="bpm-search-wrapper">
                      <i className="fas fa-search"></i>
                      <input
                        type="text"
                        placeholder="Search instruments..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bpm-search-input"
                      />
                    </div>
                    
                    <select
                      value={instrumentFilter}
                      onChange={(e) => setInstrumentFilter(e.target.value)}
                      className="bpm-filter-select"
                    >
                      <option value="all">All Types</option>
                      <option value="forex">Forex</option>
                      <option value="crypto">Crypto</option>
                      <option value="stocks">Stocks</option>
                    </select>
                  </div>
                </div>
                
                <div className="bpm-bulk-actions">
                  <button 
                    className="bpm-btn-small"
                    onClick={handleSelectAll}
                  >
                    {selectedInstruments.length === filteredInstruments.length ? 'Deselect All' : 'Select All Visible'}
                  </button>
                  
                  {instrumentTypes.slice(1).map(type => (
                    <button 
                      key={type}
                      className="bpm-btn-small"
                      onClick={() => handleSelectByType(type)}
                    >
                      Select All {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
                
                <div className="bpm-instruments-list">
                  {filteredInstruments.map(instrument => (
                    <div 
                      key={instrument._id} 
                      className={`bpm-instrument-item ${selectedInstruments.includes(instrument._id) ? 'selected' : ''}`}
                      onClick={() => handleInstrumentSelect(instrument._id)}
                    >
                      <div className="bpm-instrument-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedInstruments.includes(instrument._id)}
                          onChange={() => handleInstrumentSelect(instrument._id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      
                      <div className="bpm-instrument-info">
                        <div className="bpm-instrument-symbol">{instrument.symbol}</div>
                        <div className="bpm-instrument-description">{instrument.description}</div>
                      </div>
                      
                      <div className="bpm-instrument-type">
                        <span className={`bpm-type-badge ${instrument.type.toLowerCase()}`}>
                          {instrument.type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {filteredInstruments.length === 0 && (
                  <div className="bpm-empty">
                    <i className="fas fa-search"></i>
                    <p>No instruments found matching your criteria</p>
                  </div>
                )}
              </div>

              {/* Step 3: Options */}
              <div className="bpm-section">
                <div className="bpm-section-header">
                  <h3 className="bpm-section-title">
                    <span className="bpm-step-badge">3</span>
                    Options
                  </h3>
                </div>
                
                <div className="bpm-options">
                  <label className="bpm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={mergeHolidays}
                      onChange={(e) => setMergeHolidays(e.target.checked)}
                    />
                    <span className="bpm-checkbox-custom"></span>
                    Merge holidays (keep existing holidays and add preset holidays)
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bpm-footer">
          <div className="bpm-footer-options">
            {!(results.successful.length > 0 || results.failed.length > 0) && (
              <label className="bpm-checkbox-label">
                <input
                  type="checkbox"
                  checked={mergeHolidays}
                  onChange={(e) => setMergeHolidays(e.target.checked)}
                />
                <span className="bpm-checkbox-custom"></span>
                Merge holidays
              </label>
            )}
          </div>
          
          <div className="bpm-footer-actions">
            <button 
              className="bpm-btn-secondary" 
              onClick={onClose}
              disabled={applying}
            >
              {results.successful.length > 0 || results.failed.length > 0 ? 'Close' : 'Cancel'}
            </button>
            
            {!(results.successful.length > 0 || results.failed.length > 0) && (
              <button 
                className="bpm-btn-primary" 
                onClick={handleApplyPreset}
                disabled={applying || !selectedPreset || selectedInstruments.length === 0}
              >
                {applying ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Applying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic"></i>
                    Apply to {selectedInstruments.length} Instrument{selectedInstruments.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkPresetModal; 